import { useEffect, useRef, useState } from 'react'
import { Check, Plus, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { useCreateTestSuites } from '@/hooks'

const MAX_ROWS = 20

interface SuiteRow {
  id: number
  value: string
  showError: boolean
}

interface CreateTestSuitesDialogProps {
  projectId: string
  testPlanId: string
  testPlanIdentifier: string
  testPlanTitle: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateTestSuitesDialog({
  projectId,
  testPlanId,
  testPlanIdentifier,
  testPlanTitle,
  open,
  onOpenChange,
}: CreateTestSuitesDialogProps) {
  const [rows, setRows] = useState<SuiteRow[]>([{ id: 0, value: '', showError: false }])
  const createTestSuites = useCreateTestSuites(projectId, testPlanId)
  const rowsContainerRef = useRef<HTMLDivElement>(null)
  const prevRowsCount = useRef(rows.length)

  useEffect(() => {
    if (rows.length > prevRowsCount.current && rowsContainerRef.current) {
      rowsContainerRef.current.scrollTo({
        top: rowsContainerRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
    prevRowsCount.current = rows.length
  }, [rows.length])

  const filledCount = rows.filter(r => r.value.trim().length > 0).length
  const hasAnyFilled = filledCount > 0

  const handleAddRow = () => {
    if (rows.length >= MAX_ROWS) return
    setRows(prev => [
      ...prev,
      { id: (prev.at(-1)?.id ?? 0) + 1, value: '', showError: false },
    ])
  }

  const handleRemoveRow = (id: number) => {
    if (rows.length <= 1) return
    setRows(prev => prev.filter(r => r.id !== id))
  }

  const handleChange = (id: number, value: string) => {
    setRows(prev =>
      prev.map(r => (r.id === id ? { ...r, value, showError: false } : r)),
    )
  }

  const handleSubmit = () => {
    const trimmed = rows.map(r => r.value.trim())
    const allEmpty = trimmed.every(t => t.length === 0)

    if (allEmpty) {
      setRows(prev => prev.map(r => ({ ...r, showError: true })))
      return
    }

    const titles = trimmed.filter(t => t.length > 0)

    createTestSuites.mutate(
      { titles },
      {
        onSuccess: (res) => {
          toast.success(
            `${res.createdTestSuites.length} test suite${res.createdTestSuites.length === 1 ? '' : 's'} created`,
          )
          onOpenChange(false)
        },
        onError: () => {
          toast.error('Failed to create test suites')
        },
      },
    )
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Create New Test Suite(s)</DialogTitle>
        </DialogHeader>

        <div className="space-y-1 py-1">
          <p className="text-sm text-muted-foreground">Test Plan</p>
          <p
            className="text-sm font-medium truncate"
            title={`#${testPlanIdentifier} ${testPlanTitle}`}
          >
            <span className="text-primary">#{testPlanIdentifier}</span> {testPlanTitle}
          </p>
        </div>

        <div className="space-y-3 py-2">
          <Label>
            Test Suite Name <span className="text-red-500">*</span>
          </Label>

          <div
            ref={rowsContainerRef}
            className="max-h-[260px] space-y-3 overflow-y-auto p-1"
          >
          {rows.map((row, idx) => {
            const isLast = rows.length === 1
            const removeDisabled = isLast
            return (
              <div key={row.id} className="space-y-1">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Suite name"
                    value={row.value}
                    onChange={(e) => handleChange(row.id, e.target.value)}
                    maxLength={500}
                    aria-invalid={row.showError}
                    aria-label={`Test suite name ${idx + 1}`}
                    data-testid={`test-suite-name-input-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveRow(row.id)}
                    disabled={removeDisabled}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Remove test suite row ${idx + 1}`}
                    data-testid={`test-suite-remove-${idx}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {row.showError && (
                  <p
                    role="alert"
                    className="text-xs text-destructive"
                    data-testid={`test-suite-error-${idx}`}
                  >
                    Mandatory field
                  </p>
                )}
              </div>
            )
          })}
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAddRow}
            disabled={rows.length >= MAX_ROWS}
            className="px-2 text-primary hover:text-primary"
            data-testid="test-suite-add-another"
          >
            <Plus className="mr-1 h-4 w-4" />
            Another Test Suite
          </Button>
        </div>

        <DialogFooter className="items-center justify-between sm:justify-between">
          {hasAnyFilled && filledCount < rows.length ? (
            <p className="text-xs text-muted-foreground" data-testid="test-suite-blank-warning">
              {filledCount} of {rows.length} rows named · blank rows will be skipped
            </p>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createTestSuites.isPending}
              data-testid="create-test-suites-submit"
            >
              <Check className="h-4 w-4" />
              {createTestSuites.isPending ? 'Creating...' : 'Create Test Suite(s)'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
